/**
 * StudentEntitlementsTab — embedded inside StudentDetail.tsx.
 *
 * Lists the student's entitlements with EntitlementCard, and exposes
 * Assign Package + Freeze/Unfreeze/Cancel actions via styled, testable
 * ConfirmReasonModal dialogs (H7 — replaces window.prompt/confirm).
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';

import { Button, Input } from '@/components/common';
import { ApiError } from '@core/api/client';
import {
  useStudentEntitlements,
  useFreezeEntitlement,
  useUnfreezeEntitlement,
  useCancelEntitlement,
  type EntitlementResponse,
} from '@features/catalog/api/entitlements';
import { EntitlementCard } from '@features/catalog/components/EntitlementCard';
import { AssignPackageModal } from '@features/catalog/components/AssignPackageModal';
import { ConfirmReasonModal } from '@features/catalog/components/ConfirmReasonModal';

interface Props {
  studentId: string;
}

function defaultFreezeUntilIso(): string {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().slice(0, 10);
}

export const StudentEntitlementsTab: React.FC<Props> = ({ studentId }) => {
  const [assignOpen, setAssignOpen] = useState(false);
  const [freezeTarget, setFreezeTarget] = useState<EntitlementResponse | null>(null);
  const [freezeUntil, setFreezeUntil] = useState<string>(defaultFreezeUntilIso());
  const [cancelTarget, setCancelTarget] = useState<EntitlementResponse | null>(null);

  const { data: entitlements, isLoading, isError, refetch } = useStudentEntitlements(studentId);
  const freezeMut = useFreezeEntitlement();
  const unfreezeMut = useUnfreezeEntitlement();
  const cancelMut = useCancelEntitlement();

  const openFreeze = (e: EntitlementResponse) => {
    setFreezeUntil(defaultFreezeUntilIso());
    setFreezeTarget(e);
  };

  const submitFreeze = async (reason: string) => {
    if (!freezeTarget) return;
    try {
      await freezeMut.mutateAsync({
        id: freezeTarget.id,
        body: { until: freezeUntil, reason },
      });
      toast.success('Entitlement frozen');
      setFreezeTarget(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Freeze failed');
    }
  };

  const handleUnfreeze = async (e: EntitlementResponse) => {
    try {
      await unfreezeMut.mutateAsync(e.id);
      toast.success('Entitlement unfrozen');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Unfreeze failed');
    }
  };

  const submitCancel = async (reason: string) => {
    if (!cancelTarget) return;
    try {
      await cancelMut.mutateAsync({ id: cancelTarget.id, body: { reason } });
      toast.success('Entitlement cancelled');
      setCancelTarget(null);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Cancel failed');
    }
  };

  return (
    <div data-testid="candeza-catalog-student-entitlements-tab" data-flow="entitlements-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Entitlements</h3>
        <Button
          data-testid="candeza-catalog-student-entitlements-assign-btn"
          data-flow="entitlement-assign-btn"
          onClick={() => setAssignOpen(true)}
        >
          Assign Package
        </Button>
      </div>

      {isLoading && (
        <div data-testid="candeza-catalog-student-entitlements-loading" style={{ color: '#64748b' }}>
          Loading entitlements…
        </div>
      )}

      {isError && (
        <div data-testid="candeza-catalog-student-entitlements-error">
          Failed to load.{' '}
          <Button size="sm" variant="outline" onClick={() => refetch()}>Retry</Button>
        </div>
      )}

      {!isLoading && !isError && (entitlements?.length ?? 0) === 0 && (
        <div data-testid="candeza-catalog-student-entitlements-empty" style={{ color: '#64748b' }}>
          No entitlements yet — assign a package to get started.
        </div>
      )}

      {(entitlements ?? []).map((e) => (
        <EntitlementCard
          key={e.id}
          entitlement={e}
          onFreeze={openFreeze}
          onUnfreeze={handleUnfreeze}
          onCancel={(ent) => setCancelTarget(ent)}
        />
      ))}

      <AssignPackageModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        studentId={studentId}
      />

      <ConfirmReasonModal
        isOpen={freezeTarget !== null}
        onClose={() => setFreezeTarget(null)}
        onConfirm={submitFreeze}
        title="Freeze entitlement"
        description={
          freezeTarget
            ? `Pause "${freezeTarget.package_snapshot.name}" until the date below. Sessions cannot be consumed while frozen.`
            : undefined
        }
        reasonLabel="Reason for freeze"
        confirmLabel="Freeze"
        testIdPrefix="candeza-catalog-entitlement-freeze"
        busy={freezeMut.isPending}
        extraFields={
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 4 }}>
              Frozen until
            </label>
            <Input
              type="date"
              value={freezeUntil}
              min={new Date().toISOString().slice(0, 10)}
              onChange={(e) => setFreezeUntil(e.target.value)}
              data-testid="candeza-catalog-entitlement-freeze-until-input"
            />
          </div>
        }
      />

      <ConfirmReasonModal
        isOpen={cancelTarget !== null}
        onClose={() => setCancelTarget(null)}
        onConfirm={submitCancel}
        title="Cancel entitlement"
        description={
          cancelTarget
            ? `Cancel "${cancelTarget.package_snapshot.name}"? This is irreversible.`
            : undefined
        }
        reasonLabel="Reason for cancellation"
        confirmLabel="Cancel entitlement"
        testIdPrefix="candeza-catalog-entitlement-cancel"
        busy={cancelMut.isPending}
      />
    </div>
  );
};

